import { AxiosResponse } from 'axios'
import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useQuery } from '@apollo/client'
import { GraphQLError } from 'graphql'
import { useFormatMessage } from '@babylon/intl'
import { login as loginRequest } from '@babylon/babylon-auth'
import { useModalActions } from '@babylon/medkit'
import { useAuth0 } from '@auth0/auth0-react'

import { useDisclosure } from '@babylon/web-platform-ui'
import {
  FormRenderer,
  EligibilityModal,
  EligibilityModalUseDnaV2Design,
  ServiceNotAvailableModal,
  ServiceNotAvailableModalUseDnaV2Design,
} from '@/components'
import { OnChangeParams } from '@/components/FormRenderer/FormRenderer'
import {
  useRegisterUser,
  useApplyCode,
  useTrackingScreenTitle,
  useHandleExistingUser,
} from '@/hooks'
import { useConfig } from '@/redux/partnerConfig/selectors'
import { useRegionInfo } from '@/redux/regionInfo/selectors'
import { useNextAction, useActiveStepIndex } from '@/redux/signup/selectors'
import {
  useUserProfile,
  useUserEligibility,
  useUserExists,
} from '@/redux/userProfile/selectors'
import {
  setUserProfile,
  setUserEligibility,
  setUserExists,
} from '@/redux/userProfile/actions'
import { trackTealiumEvent } from '@/tracking'
import { Field } from '@/types/partnerConfig'
import { RegistrationFormInput, FieldControllers, LoginResponse } from '@/types'
import {
  isUserEligible as checkUserEligibility,
  filterUserObject,
  isPossiblyJson,
  shouldSkipRedemptionErrors,
  extractErrorsForTracking,
  replaceString,
  isEmailTaken,
} from '@/utils'
import messages from './RegistrationPage.messages'
import DEFAULT from '@/config/premadeConfigs'
import ConsumerNetworkUuidFromCode from '@/queries/ConsumerNetworkUuidFromCode'
import {
  setConsumerNetworkUuid,
  setMembershipCode,
} from '@/redux/userConfig/actions'
import { useMembershipCode } from '@/redux/userConfig/selectors'
import { authTokenStorageKey } from '@/constants'
import isInterimAccessToken from '@/utils/isInterimAccessToken'

type ConsumerNetworkUuidResponse = {
  consumerNetworkUuidFromCode: {
    consumer_network_uuid: string
  }
}

const stripNotNecessaryFields = (fields: Field[], fieldsToStrip: string[]) =>
  fields.filter((field) => fieldsToStrip.indexOf(field.name) < 0)

const RegistrationPageOIDC = () => {
  const dispatch = useDispatch()
  const { openModal, closeModal } = useModalActions()
  const [
    CurrentMedkitModalComponent,
    setCurrentMedkitModalComponent,
  ] = useState<JSX.Element>(<EligibilityModal />)
  const translate = useFormatMessage()
  const {
    fields,
    codes: { membership, promo },
    useDnaV2Design,
  } = useConfig()
  const membershipCode = useMembershipCode()
  const nextAction = useNextAction()
  const activeStepIndex = useActiveStepIndex()
  const {
    id: regionId,
    iso_code: isoCode,
    default_phone_country_code: defaultPhoneCountryCode,
  } = useRegionInfo()
  const { registerUser, updateUser } = useRegisterUser()
  const patient = useUserProfile()
  const { redeemCode } = useApplyCode()
  const isUserLoggedIn = !!patient.id
  const isUserEligible = useUserEligibility()
  const stepTrackingName = useTrackingScreenTitle()
  const { isAuthenticated } = useAuth0()
  const handleExistingUser = useHandleExistingUser()
  const eligibilityModalMethods = useDisclosure()
  const serviceNotAvailableModalMethods = useDisclosure()
  const [currentDnaModalMethods, setDnaModalMethods] = useState(
    eligibilityModalMethods
  )
  const userExist = useUserExists()
  const [token] = useState(sessionStorage.getItem(authTokenStorageKey))
  const [isInterim, setIsInterim] = useState(false)

  useEffect(() => {
    if (token) {
      setIsInterim(isInterimAccessToken(token))
    }
  }, [token, setIsInterim])

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(setUserExists(true))
    }
  }, [dispatch, isAuthenticated])

  const {
    data: { consumerNetworkUuidFromCode } = {},
  } = useQuery<ConsumerNetworkUuidResponse>(ConsumerNetworkUuidFromCode, {
    variables: { code: membershipCode },
    skip: !membershipCode,
  })

  const fieldControllers: FieldControllers = {
    membershipCode: (code: string) => {
      dispatch(setMembershipCode(code))
      trackTealiumEvent({
        screen_title: stepTrackingName,
        eventCategory: 'signup',
        eventAction: 'select gp',
        providerId: code,
        tealium_event: 'select gp',
      })
    },
  }

  const fieldsIncludePhoneCountryCode = !!fields[activeStepIndex].filter(
    (field) => field.name === DEFAULT.FIELDS.PHONE_COUNTRY_CODE.name
  ).length

  const initialValues: Partial<RegistrationFormInput> = {
    region_id: Number(regionId!),
    ...patient,
    ...(isoCode === 'USA' && {
      date_of_birth_us: patient.date_of_birth || '',
    }),
    agreedTAndCs: [],
    agreedPrivacyPolicy: [],
    agreedExplicitConsent: [],
    agreedMarketing: [],
    agreedHIPAAAuthorization: [],
    agreedConsumerNetworkNotice: [],
    ...(fieldsIncludePhoneCountryCode && {
      phone_country_code: patient.phone_country_code || defaultPhoneCountryCode,
    }),
  }

  const trackRegistrationError = (errorsToBeTracked?: string[]) => {
    if (errorsToBeTracked && errorsToBeTracked.length > 0) {
      errorsToBeTracked.map((error) =>
        trackTealiumEvent({
          screen_title: stepTrackingName,
          eventCategory: 'signup',
          eventAction: 'registration error',
          eventLabel: replaceString(error, '_', ' ').join(''),
          tealium_event: 'registration error',
        })
      )
    }
  }

  useEffect(() => {
    if (isUserEligible === false) {
      if (useDnaV2Design) {
        currentDnaModalMethods.onOpen()
      } else {
        openModal(CurrentMedkitModalComponent)
      }
    } else {
      currentDnaModalMethods.onClose()
      closeModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserEligible])

  useEffect(() => {
    // Reset User Eligibility for every submission
    dispatch(setUserEligibility(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (membership) {
      dispatch(setMembershipCode(membership))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membership])

  useEffect(() => {
    const cnUuid = consumerNetworkUuidFromCode?.consumer_network_uuid

    if (cnUuid) {
      dispatch(setConsumerNetworkUuid(cnUuid))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerNetworkUuidFromCode])

  const onSubmit = async (payload: RegistrationFormInput) => {
    const { email, password } = payload

    try {
      let loggedInUserId = patient.id

      if (!isUserLoggedIn) {
        const registerResponse = await registerUser(payload, membershipCode)

        const { registerUser: userProfile } = registerResponse.data || {}

        const loginResponse = await loginRequest(email!, password!, 'Patient')
        const {
          data: { userId },
        } = loginResponse as AxiosResponse<LoginResponse>

        loggedInUserId = String(userId)

        dispatch(setUserProfile(filterUserObject(userProfile!)))

        trackTealiumEvent({
          screen_title: stepTrackingName,
          eventCategory: 'signup',
          eventAction: 'completed signup',
          eventLabel: 'email',
          patient_id: String(userId),
          tealium_event: 'completed signup',
        })
      } else {
        await updateUser(payload)
        await redeemCode({
          code: membershipCode,
          userId: loggedInUserId,
          context: {
            showFeedback: false,
          },
        })
      }

      if (promo) {
        await redeemCode({
          code: promo,
          userId: loggedInUserId,
        })
      }

      nextAction()
    } catch (e) {
      // Parsing GraphQL error
      const parsedErrors = e.graphQLErrors[1]?.parsedError
      const isExistingUser = isEmailTaken(parsedErrors)

      if (isExistingUser && email) {
        handleExistingUser(email)

        return
      }

      const postcodeEligibilityError = e.graphQLErrors.find(
        (err: GraphQLError) => err.extensions?.isEligibilityError
      )

      if (postcodeEligibilityError) {
        if (useDnaV2Design) {
          currentDnaModalMethods.onClose()
          setDnaModalMethods(serviceNotAvailableModalMethods)
        } else {
          setCurrentMedkitModalComponent(<ServiceNotAvailableModal />)
        }

        dispatch(setUserEligibility(false))

        trackRegistrationError([postcodeEligibilityError.message])

        return
      }

      if (isPossiblyJson(e.message)) {
        const errorMessage = JSON.parse(e.message)
        const errorData = errorMessage?.response?.data
        let isFamily = false

        if (errorData?.error_details && !errorData?.cause?.error_details) {
          isFamily = true
        }

        const isEligible = checkUserEligibility({
          errorData,
          isFamilyMember: isFamily,
        })
        const shouldSkipMembershipCodeErrors =
          shouldSkipRedemptionErrors(errorData) && isAuthenticated

        if (shouldSkipMembershipCodeErrors) {
          nextAction()

          return
        }

        if (!isEligible) {
          const errorDetails = isFamily
            ? errorData?.error_details
            : errorData?.cause?.error_details

          // We only want to display the eligibility modal if there are no more
          // errors. Also,if we only have the eligibility error, we don't want to
          // show any errors below the form as an information modal will appear to
          // provide more info to the user.
          if (Object.keys(errorDetails).length === 1) {
            if (useDnaV2Design) {
              currentDnaModalMethods.onClose()
              setDnaModalMethods(eligibilityModalMethods)
            } else {
              setCurrentMedkitModalComponent(<EligibilityModal />)
            }

            dispatch(setUserEligibility(false))

            return
          }
        }

        trackRegistrationError(extractErrorsForTracking(errorData))
      }

      throw e
    }
  }

  const submitButtonText = userExist
    ? translate(messages.continue)
    : translate(messages.submit)

  const fieldsToStrip = [
    ...(isUserLoggedIn ? ['password'] : []),
    ...(!fieldsIncludePhoneCountryCode ? ['phone_country_code'] : []),
  ]

  const stepFields = stripNotNecessaryFields(
    fields[activeStepIndex],
    fieldsToStrip
  )

  const fieldList = [...stepFields]

  const onFormChange = ({ field, values }: OnChangeParams) => {
    const stepField = stepFields?.find((f) => f.name === field)
    const fieldControllerName = stepField?.controller

    if (fieldControllerName) {
      const fieldController = fieldControllers[fieldControllerName]
      // eslint-disable-next-line no-unused-expressions
      fieldController?.(values[field])
    }
  }

  return (
    <>
      <FormRenderer
        testId="registration-page-form"
        fields={fieldList}
        onSubmit={onSubmit}
        initialValues={initialValues}
        submitButtonText={submitButtonText}
        onChange={onFormChange}
        isInterimToken={isInterim}
      />
      <EligibilityModalUseDnaV2Design
        isOpen={eligibilityModalMethods.isOpen}
        onClose={eligibilityModalMethods.onClose}
      />

      <ServiceNotAvailableModalUseDnaV2Design
        isOpen={serviceNotAvailableModalMethods.isOpen}
        onClose={serviceNotAvailableModalMethods.onClose}
      />
    </>
  )
}

export default RegistrationPageOIDC
